import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import * as userManagementService from '@/services/userManagement.service';
import type { RootState } from './index';
import type {
  UserManagementData,
  UserFilters,
  UserUpdateData,
  UserRole,
} from '@/types/userManagement.types';

// ==============================================
// Types
// ==============================================

interface UserManagementState {
  users: UserManagementData[];
  selectedUser: UserManagementData | null;
  filters: UserFilters;
  loading: boolean;
  error: string | null;
}

// ==============================================
// Initial State
// ==============================================

const initialState: UserManagementState = {
  users: [],
  selectedUser: null,
  filters: {},
  loading: false,
  error: null,
};

// ==============================================
// Async Thunks
// ==============================================

export const fetchAllUsers = createAsyncThunk(
  'userManagement/fetchAll',
  async (filters: UserFilters | undefined, { rejectWithValue }) => {
    try {
      return await userManagementService.getAllUsers(filters);
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to fetch users');
    }
  }
);

export const fetchUserById = createAsyncThunk(
  'userManagement/fetchById',
  async (userId: string, { rejectWithValue }) => {
    try {
      return await userManagementService.getUserById(userId);
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to fetch user');
    }
  }
);

export const updateUser = createAsyncThunk(
  'userManagement/update',
  async ({ userId, data }: { userId: string; data: UserUpdateData }, { rejectWithValue }) => {
    try {
      return await userManagementService.updateUser(userId, data);
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to update user');
    }
  }
);

export const deleteUser = createAsyncThunk(
  'userManagement/delete',
  async (userId: string, { rejectWithValue }) => {
    try {
      await userManagementService.deleteUser(userId);
      return userId;
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to delete user');
    }
  }
);

export const verifyUserEmail = createAsyncThunk(
  'userManagement/verifyEmail',
  async (userId: string, { rejectWithValue }) => {
    try {
      return await userManagementService.verifyUserEmail(userId);
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to verify email');
    }
  }
);

export const setUserRole = createAsyncThunk(
  'userManagement/setRole',
  async ({ userId, role }: { userId: string; role: UserRole }, { rejectWithValue }) => {
    try {
      return await userManagementService.setUserRole(userId, role);
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to set user role');
    }
  }
);

export const toggleUserActive = createAsyncThunk(
  'userManagement/toggleActive',
  async (userId: string, { rejectWithValue }) => {
    try {
      return await userManagementService.toggleUserActive(userId);
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to toggle user active status');
    }
  }
);

export const toggleCanEditTrafficIndex = createAsyncThunk(
  'userManagement/toggleCanEditTrafficIndex',
  async (userId: string, { rejectWithValue }) => {
    try {
      return await userManagementService.toggleCanEditTrafficIndex(userId);
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to toggle traffic index permission');
    }
  }
);

export const toggleIsHangmanGameMaster = createAsyncThunk(
  'userManagement/toggleIsHangmanGameMaster',
  async (userId: string, { rejectWithValue }) => {
    try {
      return await userManagementService.toggleIsHangmanGameMaster(userId);
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to toggle Game Master privilege');
    }
  }
);

// ==============================================
// Slice
// ==============================================

const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<UserFilters>) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all users
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch user by ID
      .addCase(fetchUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUser = action.payload;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update user
      .addCase(updateUser.fulfilled, (state, action) => {
        const index = state.users.findIndex((u) => u.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        if (state.selectedUser?.id === action.payload.id) {
          state.selectedUser = action.payload;
        }
      })
      // Delete user
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users = state.users.filter((u) => u.id !== action.payload);
        if (state.selectedUser?.id === action.payload) {
          state.selectedUser = null;
        }
      })
      // Verify email
      .addCase(verifyUserEmail.fulfilled, (state, action) => {
        const index = state.users.findIndex((u) => u.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        if (state.selectedUser?.id === action.payload.id) {
          state.selectedUser = action.payload;
        }
      })
      // Set role
      .addCase(setUserRole.fulfilled, (state, action) => {
        const index = state.users.findIndex((u) => u.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        if (state.selectedUser?.id === action.payload.id) {
          state.selectedUser = action.payload;
        }
      })
      // Toggle active
      .addCase(toggleUserActive.fulfilled, (state, action) => {
        const index = state.users.findIndex((u) => u.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        if (state.selectedUser?.id === action.payload.id) {
          state.selectedUser = action.payload;
        }
      })
      // Toggle canEditTrafficIndex
      .addCase(toggleCanEditTrafficIndex.fulfilled, (state, action) => {
        const index = state.users.findIndex((u) => u.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        if (state.selectedUser?.id === action.payload.id) {
          state.selectedUser = action.payload;
        }
      })
      // Toggle isHangmanGameMaster
      .addCase(toggleIsHangmanGameMaster.fulfilled, (state, action) => {
        const index = state.users.findIndex((u) => u.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        if (state.selectedUser?.id === action.payload.id) {
          state.selectedUser = action.payload;
        }
      });
  },
});

// ==============================================
// Actions
// ==============================================

export const { setFilters, clearFilters, clearError, clearSelectedUser } =
  userManagementSlice.actions;

// ==============================================
// Selectors
// ==============================================

export const selectUsers = (state: RootState) => state.userManagement.users;
export const selectSelectedUser = (state: RootState) => state.userManagement.selectedUser;
export const selectFilters = (state: RootState) => state.userManagement.filters;
export const selectLoading = (state: RootState) => state.userManagement.loading;
export const selectError = (state: RootState) => state.userManagement.error;

export default userManagementSlice.reducer;
